import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import styled from "@emotion/styled";
import {
  getPanelElement,
  getPanelGroupElement,
  ImperativePanelHandle,
  PanelGroup,
  PanelGroupProps,
  PanelResizeHandle,
} from "react-resizable-panels";
import useMeasure from "react-use/lib/useMeasure";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import useThrottledCallback from "@/domains/react/useThrottledCallback";
import { MDSPanel } from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import { ActiveSessions } from "@/components/active-session";

interface SplitViewProps extends PropsWithChildren {
  className?: string;
  style?: React.CSSProperties;
  hasBorder?: boolean;
  animate?: boolean;
  transition?: boolean;
  dataTestId?: string;
  sidePanel?: ReactNode;
}

const SPLIT_VIEW_PANEL_GROUP_ID = "split-view-panel-group";
const DEFAULT_SIDE_PANEL_PERCENT = 30;
const MAX_SIDE_PANEL_WIDTH = 600;
const MIN_SIDE_PANEL_WIDTH = MDSPanelWidths.min;

export const SplitView: FC<SplitViewProps> = observer(
  ({ children, hasBorder = false, dataTestId = "", sidePanel }) => {
    const { store } = useAppStore();
    const { isSidePanelOpen, isMainPanelOpen, isSplitView, canRenderSplitView } = store.sidePanel;
    const showResizeHandle =
      isSplitView && canRenderSplitView && isMainPanelOpen && isSidePanelOpen;

    const [setContainerRef, { width: containerWidth }] = useMeasure<HTMLElement>();
    useLayoutEffect(() => {
      const el = getPanelGroupElement(SPLIT_VIEW_PANEL_GROUP_ID);
      if (el) setContainerRef(el);
    });

    const [setSidePanelRef, { width: sidePanelWidth }] = useMeasure<HTMLElement>();
    useEffect(() => {
      store.sidePanel.setSidePanelWidth(Math.ceil(sidePanelWidth));
    }, [sidePanelWidth, store.sidePanel]);
    const setSidePanelHandleRef = useCallback(
      (_: ImperativePanelHandle) => {
        const el = getPanelElement("side-panel");
        if (el) setSidePanelRef(el);
      },
      [setSidePanelRef]
    );

    const focusMainPanel = useThrottledCallback(() => {
      store.sidePanel.setSidePanelFocused(false);
    }, 500);

    const focusSidePanel = useThrottledCallback(() => {
      store.sidePanel.setSidePanelFocused(true);
    }, 500);

    const sidePanelMinPercent = useMemo(() => {
      if (!containerWidth) return DEFAULT_SIDE_PANEL_PERCENT;
      if (!isSidePanelOpen) return 0;
      return (MIN_SIDE_PANEL_WIDTH / containerWidth) * 100;
    }, [containerWidth, isSidePanelOpen]);

    const sidePanelMaxPercent = useMemo(() => {
      if (!containerWidth) return DEFAULT_SIDE_PANEL_PERCENT;
      if (!isMainPanelOpen) return 100;
      return (MAX_SIDE_PANEL_WIDTH / containerWidth) * 100;
    }, [containerWidth, isMainPanelOpen]);

    return (
      <Container
        id={SPLIT_VIEW_PANEL_GROUP_ID}
        direction="horizontal"
        hasSidePanel={isSplitView}
        data-component="SplitView"
      >
        {isMainPanelOpen && (
          <MainPanel
            id="main"
            order={1}
            data-test-id={dataTestId}
            hasBorder={hasBorder}
            onFocus={focusMainPanel}
            onClick={focusMainPanel}
            defaultSize={100 - sidePanelMinPercent}
            data-component="MainPanel"
          >
            <CenterPanel data-component="CenterPanel">
              {children}
              <ActiveSessions />
            </CenterPanel>
          </MainPanel>
        )}
        {showResizeHandle && (
          <StyledPanelResizeHandle data-component="StyledPanelResizeHandle">
            <ResizeHandle data-component="ResizeHandle" />
          </StyledPanelResizeHandle>
        )}
        {isSidePanelOpen && (
          <SidePanelContainer
            id="side-panel"
            order={2}
            ref={setSidePanelHandleRef}
            onFocus={focusSidePanel}
            onClick={focusSidePanel}
            data-component="SidePanelContainer"
            defaultSize={sidePanelMinPercent}
            minSize={sidePanelMinPercent}
            maxSize={sidePanelMaxPercent}
          >
            {sidePanel}
          </SidePanelContainer>
        )}
      </Container>
    );
  }
);

interface ContainerProps extends PanelGroupProps {
  hasSidePanel: boolean;
}

const Container = styled(PanelGroup, {
  shouldForwardProp: prop => prop !== "hasSidePanel",
})<ContainerProps>(({ hasSidePanel }) =>
  hasSidePanel
    ? {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        width: "100%",
        gap: "2px",
      }
    : {
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        justifyContent: "center",
        margin: "0 auto",
        maxWidth: "100%",
        width: "100%",
      }
);

interface MainPanelProps {
  hasBorder: boolean;
}

const MainPanel = styled(MDSPanel, {
  shouldForwardProp: prop => prop !== "hasBorder",
})<MainPanelProps>(({ hasBorder, theme }) => ({
  alignSelf: "center",
  boxSizing: "border-box",
  display: "flex",
  height: "100%",
  overflow: "hidden",
  border: "none",

  ...(hasBorder
    ? {
        "@media (max-width: 899px)": {
          width: "100%",
        },

        "@media (min-width: 900px)": {
          border: `1px solid ${theme.colors.border.default}`,
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: "0px 2px 4px rgba(69, 79, 104, 0.1)",
        },
      }
    : {
        "@media (max-width: 900px)": {
          width: "calc(100% - 16px)",
        },
      }),
}));

const CenterPanel = styled.div({
  boxSizing: "border-box",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
});

const StyledPanelResizeHandle = styled(PanelResizeHandle)`
  border-radius: 100px;
  cursor: ew-resize;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  &:hover, &[data-resize-handle-state="hover"], &[data-resize-handle-state="drag"] {
    background: ${({ theme }) => theme.colors.surface.pane};
    transition: 0.2s ease-in;
    border-color: ${({ theme }) => theme.colors.border.default};

    "> div": {
      border-color: ${({ theme }) => theme.colors.border.default};
    },
  },
`;

const ResizeHandle = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  overflow: hidden;
  width: 2px;
`;

const SidePanelContainer = styled(MDSPanel)`
  position: relative;
`;
